// What an Admin can do to one person's record, and what has been done to it
// (decision C4, 12 Oct; migration 152).
//
// FOUR ACTIONS, AN ADMIN'S ALONE, EACH BEHIND THE AUTHENTICATOR. Withhold
// leaves somebody out of every file from here on and keeps them on the show.
// Block refuses their address a passcode, a registration and a play at this
// show. Correct records new details beside the old ones. Erase removes their
// details from everywhere this console shows people and cannot be undone. The
// first two are reversible and say so; the confirms are the October
// prototype's (console-app.jsx, personAct). Erase keeps the prototype's words
// where they are now true ("timed and attributed to you", "does not recall
// anything that has left") and says what the service does since 153: it
// deletes in place, everywhere, and the backups age out on their own schedule.
//
// WHO IS OFFERED THEM IS roleAccess.ts's `personActions`, tied there to the
// guard on the route. An Editor and a Viewer read the record and its history
// and are offered no button: a control that looks like it works is how
// somebody concludes a person has been withheld (People.tsx's old note).

import { useState, type ReactNode } from "react";
import { DefList } from "@nova/desk-ui/parts";
import { SecLabel } from "@nova/desk-ui/kit";
import { actOnAttendee, retryRideErasure, type AdminRefusal } from "../api/admin.js";
import type { ApiOptions } from "../api/client.js";
import type { AttendeeAction, AttendeeErasure, AttendeeRegistration, AttendeeStanding } from "../api/attendees.js";
import { moment } from "../operator/present.js";
import type { SessionToken } from "../session/store.js";
import { AdminRefused, Authorise } from "../surfaces/Authorise.js";
import { Dialog } from "../surfaces/Dialog.js";
import { opens } from "../surfaces/roleAccess.js";

type Kind = "withhold" | "release" | "block" | "unblock" | "correct" | "erase";

type Ask = {
  readonly title: string;
  readonly verb: string;
  readonly body: readonly string[];
  /** The service records these with a reason and refuses them without one. */
  readonly needsReason: boolean;
  /** What the page says once it is done. */
  readonly done: string;
};

/** The word an erasure is confirmed with, typed, because it does not come back. */
export const ERASE_WORD = "ERASE";

export const PERSON_ASKS: Readonly<Record<Kind, Ask>> = {
  withhold: {
    title: "Withhold this person from every file?",
    verb: "Withhold them",
    body: [
      "They stay on this activation with their consents intact and leave every file produced from here on.",
      "A file already produced does not change, because it has already been sent. This is reversible and is recorded with a reason.",
    ],
    needsReason: true,
    done: "Withheld. The next file produced leaves them out; a file already produced does not change.",
  },
  release: {
    title: "Put this person back in files?",
    verb: "Put them back",
    body: [
      "The next file produced includes them again, under the same consent rules as everybody else.",
      "A file produced while they were withheld does not change.",
    ],
    needsReason: false,
    done: "Put back. The next file produced includes them again.",
  },
  block: {
    title: "Refuse a new passcode to this address?",
    verb: "Refuse it",
    body: [
      "That address can no longer sign in on the stand, and is refused a registration and a play at this show. Nothing already registered is removed.",
      "Reversible, and recorded against your sign-in.",
    ],
    needsReason: true,
    done: "A refused address is recorded against your sign-in. Nothing already registered is removed.",
  },
  unblock: {
    title: "Lift the refusal on this address?",
    verb: "Lift it",
    body: ["That address can ask for a passcode, register and play at this show again."],
    needsReason: false,
    done: "Lifted. That address can register at this show again.",
  },
  correct: {
    title: "Correct this person's details?",
    verb: "Record the correction",
    body: [
      "A correction is a new record alongside the old one, never an overwrite. What each field said before is kept with it, against your sign-in.",
      "Every page and every file produced from here on shows the corrected details. A file already produced does not change.",
    ],
    needsReason: true,
    done: "Corrected. Every page and every file produced from here on shows the new details.",
  },
  erase: {
    title: "Erase this person's details?",
    verb: "Erase them",
    body: [
      "Their name, addresses, phone and form answers are deleted from the service's record of them, the Entry list, the photo booth and its photographs, the prize wheel and Ride to Survive. Their registration stays in the counts as a record with nobody's details on it. It is timed and attributed to you.",
      "Every tablet on this show is told to delete them when it next syncs. Copies in the service's backups are not edited: they age out on the backups' normal schedule. If their address was refused here, it stays refused.",
      "This cannot be undone. A file already produced with this person in it does not change: erasing does not recall anything that has left.",
    ],
    needsReason: true,
    done: "Erased. Their details are deleted everywhere the service holds them, and their tablets have been told.",
  },
};

/** The four contact slots a correction may move, in the record's order and words. */
const SLOTS = [
  ["display_name", "Name"],
  ["contact_email", "Email address"],
  ["deliver_to_email", "Address anything is sent to"],
  ["phone", "Phone"],
] as const;
type Slot = (typeof SLOTS)[number][0];

/** The reason field's warning, because a reason is kept and is not personal data. */
export const REASON_WARNING = "Don't type personal details here.";

export type PersonActionsProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly attendeeRef: string;
  readonly role: string;
  readonly standing: AttendeeStanding;
  readonly registration: AttendeeRegistration;
  /** For an erased person, what the erasure reached. */
  readonly erasure?: AttendeeErasure | null;
  /** Called with the page's sentence once an action is done, so the record can be read again. */
  readonly onDone: (said: string) => void;
  readonly onSessionEnded: (detail: string) => void;
};

/** The design's "Available actions", now real for an Admin. */
export function PersonActions(props: PersonActionsProps): ReactNode {
  const { standing } = props;
  const [asking, setAsking] = useState<Kind | null>(null);
  const admin = opens({ role: props.role, canExport: null }, "personActions");

  if (standing.erased) {
    return <Erased {...props} admin={admin} />;
  }

  if (!admin) {
    return (
      <section>
        <SecLabel>Available actions</SecLabel>
        <div className="panel panel-stack">
          <p>This page is read-only for you.</p>
          <p className="help help-tight">
            Withholding someone from files, refusing their address, correcting their details and erasing them
            are an Admin&apos;s, each behind the Admin&apos;s authenticator.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <SecLabel info="Each one asks for your authenticator and is recorded against your sign-in.">Available actions</SecLabel>
      <div className="panel panel-stack">
        <div className="acts acts-flush">
          <button type="button" className="btn btn-quiet" onClick={() => setAsking(() => (standing.withheld ? "release" : "withhold"))}>
            {standing.withheld ? "Put back in files" : "Withhold from files"}
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => setAsking(() => (standing.blocked ? "unblock" : "block"))}>
            {standing.blocked ? "Lift the refusal" : "Refuse a passcode"}
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => setAsking(() => "correct")}>
            Correct details
          </button>
          <button type="button" className="btn btn-danger" onClick={() => setAsking(() => "erase")}>
            Erase this person
          </button>
        </div>
        <p className="help help-tight">
          Withholding and refusing can be undone. A correction keeps what it replaced. An erasure cannot be undone.
        </p>
      </div>
      {asking === null ? null : (
        <Confirm {...props} kind={asking} onClose={() => setAsking(() => null)} />
      )}
    </section>
  );
}

/** An erased person's record: nothing more to do, and what the erasure reached. */
function Erased({
  api,
  token,
  activationId,
  attendeeRef,
  erasure,
  admin,
  onDone,
  onSessionEnded,
}: PersonActionsProps & { readonly admin: boolean }): ReactNode {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const ride =
    erasure === null || erasure === undefined
      ? null
      : erasure.rideState === "done"
        ? `deleted${erasure.rideErased === null ? "" : `: ${erasure.rideErased} rider record${erasure.rideErased === 1 ? "" : "s"}`}`
        : erasure.rideState === "pending"
          ? `waiting on the game service, asked ${erasure.rideAttempts} time${erasure.rideAttempts === 1 ? "" : "s"}`
          : "not reached from this service";

  const retry = async (): Promise<void> => {
    setRefusal(() => null);
    setBusy(() => true);
    const result = await retryRideErasure(api, token, { activationId, attendeeRef });
    setBusy(() => false);
    if (!result.ok) {
      setRefusal(() => result);
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      return;
    }
    onDone(
      result.value.rideState === "done"
        ? "The game service has deleted them too."
        : "The game service still did not answer. Nothing else is waiting.",
    );
  };

  return (
    <section>
      <SecLabel>What the erasure reached</SecLabel>
      <div className="panel panel-stack">
        <p style={{ margin: 0 }}>This person&apos;s details were erased. Nothing further can be done to this record.</p>
        {refusal === null ? null : <AdminRefused refusal={refusal} />}
        {erasure === null || erasure === undefined ? null : (
          <DefList
            rows={[
              ["The service's record", `${erasure.spineRows} entr${erasure.spineRows === 1 ? "y" : "ies"} blanked, kept in the counts`],
              ["Entry and the photo booth", `${erasure.entryGuests} guest row${erasure.entryGuests === 1 ? "" : "s"}, ${erasure.boothMedia} photograph${erasure.boothMedia === 1 ? "" : "s"}${erasure.boothBytesDeleted === null ? "" : `, ${erasure.boothBytesDeleted} file${erasure.boothBytesDeleted === 1 ? "" : "s"} deleted`}`],
              ["Tablets on this show", `${erasure.devicesTold} told to delete them`],
              ["Ride to Survive", ride ?? ""],
              ["Backups", "not edited; they age out on their normal schedule"],
            ]}
          />
        )}
        {admin && erasure?.rideState === "pending" ? (
          <div className="acts acts-flush">
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void retry()}>
              {busy ? "Asking…" : "Try the game service again"}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function Confirm({
  api,
  token,
  activationId,
  attendeeRef,
  registration,
  kind,
  onClose,
  onDone,
  onSessionEnded,
}: PersonActionsProps & { readonly kind: Kind; readonly onClose: () => void }): ReactNode {
  const ask = PERSON_ASKS[kind];
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const [values, setValues] = useState<Record<Slot, string>>(() => ({
    display_name: registration.displayName ?? "",
    contact_email: registration.contactEmail ?? "",
    deliver_to_email: registration.deliverToEmail ?? "",
    phone: registration.phone ?? "",
  }));

  // Only the slots that moved are sent: an unchanged one is not a correction.
  const was: Record<Slot, string> = {
    display_name: registration.displayName ?? "",
    contact_email: registration.contactEmail ?? "",
    deliver_to_email: registration.deliverToEmail ?? "",
    phone: registration.phone ?? "",
  };
  const moved = SLOTS.filter(([slot]) => values[slot].trim() !== was[slot]);
  const ready =
    code.length === 6 &&
    (!ask.needsReason || reason.trim() !== "") &&
    (kind !== "correct" || moved.length > 0) &&
    (kind !== "erase" || typed.trim() === ERASE_WORD);

  const run = async (): Promise<void> => {
    setRefusal(() => null);
    setBusy(() => true);
    const result = await actOnAttendee(api, token, {
      activationId,
      attendeeRef,
      action: kind,
      reason: reason.trim() === "" ? null : reason.trim(),
      ...(kind === "correct"
        ? {
            correction: Object.fromEntries(
              moved.map(([slot]) => [slot, values[slot].trim() === "" ? null : values[slot].trim()]),
            ),
          }
        : {}),
      secondFactorCode: code,
    });
    setBusy(() => false);
    if (!result.ok) {
      setRefusal(() => result);
      // A spent or wrong code is retyped, never kept.
      setCode(() => "");
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      return;
    }
    onDone(ask.done);
    onClose();
  };

  return (
    <Dialog
      title={ask.title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={kind === "erase" ? "btn btn-danger" : "btn"}
            disabled={!ready || busy}
            onClick={() => void run()}
          >
            {busy ? "Recording…" : ask.verb}
          </button>
        </>
      }
    >
      {refusal === null ? null : <AdminRefused refusal={refusal} />}
      {ask.body.map((line, index) => (
        <p key={line} className={index === 0 ? "person-act" : undefined}>
          {line}
        </p>
      ))}
      {kind === "correct" ? (
        <div className="panel-stack">
          {SLOTS.map(([slot, label]) => (
            <label className="field" key={slot}>
              <span className="label">{label}</span>
              <input
                className="input"
                value={values[slot]}
                disabled={busy}
                onChange={(event) => {
                  const next = event.target.value;
                  setValues((now) => ({ ...now, [slot]: next }));
                }}
              />
            </label>
          ))}
        </div>
      ) : null}
      <label className="field">
        <span className="label">{ask.needsReason ? "Reason · recorded with it" : "Reason · optional"}</span>
        <span className="help help-tight">{REASON_WARNING}</span>
        <input
          className="input"
          value={reason}
          maxLength={500}
          disabled={busy}
          onChange={(event) => {
            const next = event.target.value;
            setReason(() => next);
          }}
        />
      </label>
      {kind === "erase" ? (
        <label className="field">
          <span className="label">Type {ERASE_WORD} to confirm</span>
          <input
            className="input"
            value={typed}
            disabled={busy}
            autoComplete="off"
            onChange={(event) => {
              const next = event.target.value;
              setTyped(() => next);
            }}
          />
        </label>
      ) : null}
      <Authorise value={code} onChange={(next) => setCode(() => next)} busy={busy} what={ask.verb.toLowerCase()} />
    </Dialog>
  );
}

const ACTION_WORDS: Readonly<Record<AttendeeAction["action"], string>> = {
  withhold: "Withheld from files",
  release: "Put back in files",
  block: "Address refused",
  unblock: "Refusal lifted",
  correct: "Details corrected",
  erase: "Details erased",
};

const SLOT_WORDS: Readonly<Record<string, string>> = Object.fromEntries(SLOTS);

/** Everything an Admin has done to this record, oldest first, as the service recorded it. */
export function ActionHistory({
  actions,
  note,
}: {
  readonly actions: readonly AttendeeAction[];
  readonly note: string;
}): ReactNode {
  return (
    <section>
      <SecLabel info="Each action is recorded against the Admin who took it, the session they used and the authenticator code they gave.">
        What has been done to this record
      </SecLabel>
      {actions.length === 0 ? (
        <div className="panel">
          <p style={{ margin: 0 }}>{note || "Nothing has been done to this record."}</p>
        </div>
      ) : (
        <>
          <div className="tablewrap">
            <div className="tablescroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">What</th>
                    <th scope="col">Why</th>
                    <th scope="col">What changed</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((line, index) => (
                    <tr key={`${line.at}-${index}`}>
                      <td data-label="When" className="num">
                        {moment(line.at)}
                      </td>
                      <td data-label="What">{ACTION_WORDS[line.action]}</td>
                      <td data-label="Why">
                        {line.reason ?? (
                          <span className="quiet">{line.reasonScrubbed ? "removed by the erasure" : "no reason given"}</span>
                        )}
                      </td>
                      <td data-label="What changed">
                        {line.action !== "correct" ? (
                          <span className="quiet">nothing on the record</span>
                        ) : line.correction === null ? (
                          <span className="quiet">removed by the erasure</span>
                        ) : (
                          <DefList
                            rows={Object.entries(line.correction).map(([field, change]) => [
                              SLOT_WORDS[field] ?? field,
                              `${change.from ?? "nothing"} → ${change.to ?? "nothing"}`,
                            ])}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {note === "" ? null : <p className="help help-tight">{note}</p>}
        </>
      )}
    </section>
  );
}

/** The tags a person wears for where they stand, on the list and on their record. */
export function StandingTags({ standing }: { readonly standing: AttendeeStanding }): ReactNode {
  if (standing.erased) return <span className="tag tag-off">Details erased</span>;
  return (
    <>
      {standing.withheld ? <span className="tag tag-warn">Withheld from files</span> : null}
      {standing.blocked ? <span className="tag tag-bad">Address refused</span> : null}
      {standing.corrected ? <span className="tag">Corrected</span> : null}
    </>
  );
}
